// @ts-nocheck
import { useAuth } from '@/contexts/AuthContext';
import { useAdminImpersonation } from '@/contexts/AdminImpersonationContext';

/**
 * Hook that returns the effective user data, considering admin impersonation.
 * This is the single source of truth for determining which profile to display.
 * All components should use this instead of useAuth or useEffectiveAuth directly.
 */
export const useActiveProfile = () => {
  const { profile: authProfile, user: authUser, ...authMethods } = useAuth();
  const { isImpersonating, impersonatedProfile } = useAdminImpersonation();

  // Always prioritize impersonation for admin users
  const activeProfile = isImpersonating ? impersonatedProfile : authProfile;
  const activeProfileId = activeProfile?.id;
  
  console.log('🎯 useActiveProfile:', {
    isImpersonating,
    activeProfileId,
    profileName: activeProfile?.name,
    authProfileId: authProfile?.id,
    impersonatedProfileId: impersonatedProfile?.id
  });

  return {
    user: authUser, // Always use the auth user for session/API calls
    profile: activeProfile,
    profileId: activeProfileId,
    isImpersonating,
    ...authMethods
  };
};

/**
 * Centralized function to get the active profile ID from various sources
 * Checks admin impersonation, localStorage, and falls back to current profile
 */
export const getActiveProfileId = () => {
  // First, check for admin impersonation in sessionStorage
  const adminImpersonationState = sessionStorage.getItem('admin_impersonation_state');
  if (adminImpersonationState) {
    try {
      const { impersonatedProfileId } = JSON.parse(adminImpersonationState);
      if (impersonatedProfileId) {
        console.log('🔍 getActiveProfileId - Using admin impersonated profile:', impersonatedProfileId);
        return impersonatedProfileId;
      }
    } catch (error) {
      console.error('🔍 getActiveProfileId - Error parsing admin impersonation state:', error);
    }
  }

  // Next, check for parent access or other localStorage overrides
  const activeProfileFromStorage = localStorage.getItem('activeProfileId');
  const actingAs = localStorage.getItem('actingAs');
  
  if (activeProfileFromStorage && (actingAs === 'parent' || actingAs === 'admin')) {
    console.log('🔍 getActiveProfileId - Using localStorage activeProfileId:', activeProfileFromStorage);
    return activeProfileFromStorage;
  }

  // If no overrides, return null to use the hook's logic
  console.log('🔍 getActiveProfileId - No overrides found, using hook logic');
  return null;
};